import React, {
  createContext, useState, useContext,
  useEffect, useRef, useCallback
} from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, LinearProgress, Box
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

const IDLE_WARN_MS  = 5 * 60 * 1000; // 5 min sin actividad → mostrar aviso
const IDLE_KILL_MS  = 1 * 60 * 1000; // 1 min para responder → cerrar sesión
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export const AuthProvider = ({ children }) => {
  const [user, setUser]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60); // segundos restantes

  const warnTimer    = useRef(null);
  const killTimer    = useRef(null);
  const countdownRef = useRef(null);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    clearTimeout(warnTimer.current);
    clearTimeout(killTimer.current);
    clearInterval(countdownRef.current);
    setShowWarning(false);
    await authService.logout();
    sessionStorage.removeItem("welcome_popup_shown");
    setUser(null);
  }, []);

  // ── Arrancar cuenta regresiva del dialog ──────────────────────────────────
  const startKillCountdown = useCallback(() => {
    setCountdown(60);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    killTimer.current = setTimeout(() => {
      logout();
    }, IDLE_KILL_MS);
  }, [logout]);

  // ── "Continuar sesión" desde el dialog ────────────────────────────────────
  const handleContinue = useCallback(() => {
    clearTimeout(killTimer.current);
    clearInterval(countdownRef.current);
    setShowWarning(false);
    // Reiniciar el timer de inactividad
    warnTimer.current = setTimeout(() => {
      setShowWarning(true);
      startKillCountdown();
    }, IDLE_WARN_MS);
  }, [startKillCountdown]);

  // ── Init: Validar token contra el servidor antes de confiar en caché local ────────────
  useEffect(() => {
    const initAuth = async () => {
      // Limpiar tokens legacy de localStorage (migración a sessionStorage)
      if (localStorage.getItem("token")) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("session_id");
        localStorage.removeItem("device_info");
        localStorage.removeItem("previous_session_closed");
        setLoading(false);
        return; // forzar re-login
      }

      const token = sessionStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      // Verificar con el servidor que el token sigue siendo válido
      try {
        const isValid = await authService.validateSession();
        if (isValid) {
          setUser(authService.getCurrentUser());
        } else {
          authService.clearAuthData();
        }
      } catch {
        // Error de red u otro — por seguridad limpiar sesión
        authService.clearAuthData();
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // ── Gestionar listeners según sesión activa ───────────────────────────────
  useEffect(() => {
    if (!user) {
      clearTimeout(warnTimer.current);
      clearTimeout(killTimer.current);
      clearInterval(countdownRef.current);
      return;
    }

    // Arrancar timer al iniciar sesión
    warnTimer.current = setTimeout(() => {
      setShowWarning(true);
      startKillCountdown();
    }, IDLE_WARN_MS);

    const handleActivity = () => {
      if (!showWarning) {
        clearTimeout(warnTimer.current);
        clearTimeout(killTimer.current);
        clearInterval(countdownRef.current);
        warnTimer.current = setTimeout(() => {
          setShowWarning(true);
          startKillCountdown();
        }, IDLE_WARN_MS);
      }
    };

    ACTIVITY_EVENTS.forEach(evt =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );

    return () => {
      clearTimeout(warnTimer.current);
      clearTimeout(killTimer.current);
      clearInterval(countdownRef.current);
      ACTIVITY_EVENTS.forEach(evt =>
        window.removeEventListener(evt, handleActivity)
      );
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = async (username, password) => {
    const data = await authService.login(username, password);
    setUser(data.user);
    return data;
  };

  // ── Refresh: re-leer el user desde sessionStorage (sin petición al servidor) ──
  const refreshUser = useCallback(() => {
    setUser(authService.getCurrentUser());
  }, []);

  const value = {
    user, login, logout, loading, refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.rol === 'administrador',
    isColaborador: user?.rol === 'colaborador',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* ── Dialog de advertencia de inactividad ── */}
      <Dialog
        open={showWarning}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            bgcolor: '#1a1f2e',
            border: '1px solid rgba(255,193,7,0.4)',
            borderRadius: 3,
            minWidth: 340,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#FFC107' }}>
          <AccessTimeIcon />
          Sesión por expirar
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
            Tu sesión cerrará automáticamente por inactividad en:
          </Typography>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h3" sx={{ color: countdown <= 10 ? '#f44336' : '#FFC107', fontWeight: 700 }}>
              {countdown}s
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={(countdown / 60) * 100}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: countdown <= 10 ? '#f44336' : '#FFC107',
                transition: 'none',
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={logout}
            sx={{ flex: 1 }}
          >
            Cerrar sesión
          </Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            sx={{
              flex: 1,
              bgcolor: '#FFC107',
              color: '#000',
              '&:hover': { bgcolor: '#FFB300' }
            }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
